export const cupSizeOrder = [
  "AA",
  "A",
  "B",
  "C",
  "D",
  "DD",
  "E",
  "DDD",
  "F",
  "FF",
  "G",
  "GG",
  "H",
  "HH",
  "I",
  "J",
]
const braSizeRegex = /^(\d+)\s*([A-Za-z]+)$/
const pantsSizeRegex = /^(\d+)[x/](\d+)$/i

// TODO: Consider when, where and how to make this generic
export const sizeSortingOrder = [
  "XXXS",
  "One Size",
  "XXS",
  "XX-Small",
  "XX-Small / Slim",
  "XX-Small / Regular",
  "XS",
  "X-Small",
  "X-Small / Slim",
  "X-Small / Regular",
  "XS+",
  "XS-S",
  "S",
  "Small",
  "Small / Slim",
  "Small / Regular",
  "Small / Longer",
  "S+",
  "Small / Medium",
  "S-M",
  "M",
  "Medium",
  "Medium / Slim",
  "Medium / Regular",
  "Medium / Longer",
  "M+",
  "M-L",
  "L",
  "Large",
  "Large / Slim",
  "Large / Regular",
  "Large / Longer",
  "L+",
  "Large / X-Large",
  "L-XL",
  "XL",
  "X-Large",
  "X-Large / Slim",
  "X-Large / Regular",
  "X-Large / Longer",
  "XL+",
  "XL-XXL",
  "XXL",
  "XX-Large",
  "XX-Large / Slim",
  "XX-Large / Regular",
  "XX-Large / Longer",
  "2XL",
  "XXL+",
  "XXXL",
  "XXX-Large",
  "XXX-Large / Slim",
  "XXX-Large / Regular",
  "XXX-Large / Longer",
  "3XL",
  "XXXXL",
  "XXXX-Large",
  "XXXX-Large / Slim",
  "XXXX-Large / Regular",
  "4XL",
  "XXXXXL",
  "5XL",
  "6XL",
  "7XL",
  "8XL",
  "9XL",
  "10XL",
]

const sizeOrderIndex = new Map<string, number>()
sizeSortingOrder.forEach((size, i) => {
  if (!sizeOrderIndex.has(size)) sizeOrderIndex.set(size, i)
})
const sizeOrderIndexOf = (size: string) => sizeOrderIndex.get(size) ?? -1

// Comparators call this O(n log n) times over a small size vocabulary, and each parse runs
// several regexes.
const parsedSizeCache = new Map<string, number>()
function parseSizeCached(size: string) {
  let parsed = parsedSizeCache.get(size)
  if (parsed === undefined) {
    parsed = parseSize(size)
    parsedSizeCache.set(size, parsed)
  }
  return parsed
}

export function sortSizesComparator(a: string, b: string) {
  const indexA = sizeOrderIndexOf(a)
  const indexB = sizeOrderIndexOf(b)

  if (indexA === -1 && indexB === -1) {
    if (typeof a === "string" && typeof b === "string") {
      const aStr = a as string
      const bStr = b as string

      const parsedA = parseSizeCached(aStr)
      const parsedB = parseSizeCached(bStr)

      if (parsedA === parsedB) {
        return 0
      }
      return parsedA > parsedB ? 1 : -1
    }
    return a > b ? 1 : -1
  }

  return indexA - indexB
}

function parseSize(size: string) {
  const braMatch = size.match(braSizeRegex)
  if (braMatch) {
    const band = Number(braMatch[1])
    const cup = braMatch[2]?.toUpperCase() ?? ""
    const cupIndex = cupSizeOrder.indexOf(cup)
    if (cupIndex !== -1) {
      return band * 1000 + cupIndex
    }
  }

  const pantsMatch = size.match(pantsSizeRegex)
  if (pantsMatch) {
    const waist = Number(pantsMatch[1])
    const inseam = Number(pantsMatch[2])
    return waist * 1000 + inseam
  }

  const labelMatch = size.match(/([a-zA-Z]+)/g)?.[0] ?? ""
  const numberMatch = size.match(/(\d+(?:\.\d+)?)/g)

  let weight = sizeOrderIndexOf(labelMatch) * 1_000_000
  const dropoff = 100

  numberMatch?.forEach((num, i) => {
    if (!isNaN(Number(num))) {
      weight += Number(num) * Math.pow(dropoff, numberMatch.length - i - 1)
    }
  })

  return weight
}

export function customSortComparatorByProperty<T>(
  property: keyof T,
  customOrder: Array<T[keyof T]>,
) {
  return (a: T, b: T) => {
    const indexA = customOrder.indexOf(a[property])
    const indexB = customOrder.indexOf(b[property])

    if (indexA === -1 && indexB === -1) {
      if (typeof a[property] === "string" && typeof b[property] === "string") {
        const aStr = a[property] as string
        const bStr = b[property] as string

        const parsedA = parseSizeCached(aStr)
        const parsedB = parseSizeCached(bStr)

        if (parsedA === parsedB) {
          return 0
        }
        return parsedA > parsedB ? 1 : -1
      }
      return a[property] > b[property] ? 1 : -1
    }

    return indexA - indexB
  }
}
